/**
 * @voltxampere/engine — Thermal metadata table (Sprint 59)
 *
 * Component type → thermal parameters lookup. Sourced from
 * `docs/reference/PHASE_2_LIVE_STUDIO.md` §34-2 (Vision arşiv §4
 * reference table, datasheet-validated: Vishay / ON Semi / Cree /
 * Fairchild / Infineon).
 *
 * Engine zero-diff: this is an orthogonal lookup, not a stamp
 * signature change. Existing solver + planner + ERC paths are
 * unchanged.
 *
 * Variants — when a component carries a `variant` param or specific
 * `package` param (e.g. `"to-92"`, `"to-220"`, `"signal"`, `"power"`),
 * the lookup uses the variant key. Otherwise the type's base
 * descriptor wins.
 *
 * Full Aşama 2 surface (LED explosion / capacitor swelling / heat
 * map / particle pool) ships post-launch Sprint 65+; this table is
 * the foundation those features will consume.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const J_PER_C_DEFAULT = 0.1;
/**
 * Canonical thermal lookup. Keys map to component type strings (and
 * variant-aware composites like `"resistor:1w"`, `"bjt-npn:to-220"`).
 * Components without an explicit entry use the type-level default
 * (resistor / diode / led / bjt-npn / bjt-pnp / nmos / pmos /
 * capacitor / inductor).
 */
export const THERMAL_TABLE = Object.freeze({
    // Resistors — 5 wattage tiers per Vishay datasheet.
    "resistor:1/4w": { rThermal: 200, tMax: 155, pMax: 0.25, cThermal: J_PER_C_DEFAULT },
    "resistor:1/2w": { rThermal: 150, tMax: 155, pMax: 0.5, cThermal: J_PER_C_DEFAULT },
    "resistor:1w": { rThermal: 80, tMax: 200, pMax: 1, cThermal: J_PER_C_DEFAULT * 2 },
    "resistor:2w": { rThermal: 50, tMax: 200, pMax: 2, cThermal: J_PER_C_DEFAULT * 3 },
    "resistor:5w": { rThermal: 20, tMax: 250, pMax: 5, cThermal: J_PER_C_DEFAULT * 5 },
    resistor: { rThermal: 200, tMax: 155, pMax: 0.25, cThermal: J_PER_C_DEFAULT },
    // Diodes — signal + power.
    "diode:signal": { rThermal: 350, tMax: 150, pMax: 0.5, cThermal: J_PER_C_DEFAULT },
    "diode:power": { rThermal: 50, tMax: 175, pMax: 5, cThermal: J_PER_C_DEFAULT * 5 },
    diode: { rThermal: 350, tMax: 150, pMax: 0.5, cThermal: J_PER_C_DEFAULT },
    // LEDs — signal + power.
    "led:signal": { rThermal: 100, tMax: 110, pMax: 0.1, cThermal: J_PER_C_DEFAULT * 0.5 },
    "led:power": { rThermal: 20, tMax: 120, pMax: 3, cThermal: J_PER_C_DEFAULT * 5 },
    led: { rThermal: 100, tMax: 110, pMax: 0.1, cThermal: J_PER_C_DEFAULT * 0.5 },
    // BJT — TO-92 (signal) + TO-220 (power) per Fairchild datasheet.
    "bjt-npn:to-92": { rThermal: 125, tMax: 150, pMax: 0.625, cThermal: J_PER_C_DEFAULT },
    "bjt-npn:to-220": { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    "bjt-pnp:to-92": { rThermal: 125, tMax: 150, pMax: 0.625, cThermal: J_PER_C_DEFAULT },
    "bjt-pnp:to-220": { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    "bjt-npn": { rThermal: 125, tMax: 150, pMax: 0.625, cThermal: J_PER_C_DEFAULT },
    "bjt-pnp": { rThermal: 125, tMax: 150, pMax: 0.625, cThermal: J_PER_C_DEFAULT },
    // MOSFET — TO-220 power package per Infineon.
    "nmos:to-220": { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    "pmos:to-220": { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    nmos: { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    pmos: { rThermal: 60, tMax: 150, pMax: 1.5, cThermal: J_PER_C_DEFAULT * 4 },
    // Capacitor electrolytic — uses V_max validation rather than P×R_th,
    // but a coarse R_thermal stays useful for surface heating.
    "capacitor:electrolytic": { rThermal: 80, tMax: 105, pMax: 0.3, cThermal: J_PER_C_DEFAULT * 2 },
    capacitor: { rThermal: 80, tMax: 105, pMax: 0.3, cThermal: J_PER_C_DEFAULT * 2 },
});
/**
 * Look up thermal params for a component. Tries `${type}:${variant}`
 * first (e.g. `"resistor:1w"`), then falls back to the bare type.
 * Returns null when the type has no thermal descriptor (op-amps,
 * voltage sources, ground, etc.).
 */
export function lookupThermal(type, variant) {
    if (variant !== undefined) {
        const key = `${type}:${variant.toLowerCase()}`;
        const hit = THERMAL_TABLE[key];
        if (hit !== undefined)
            return hit;
    }
    const base = THERMAL_TABLE[type];
    return base ?? null;
}
/**
 * Extract the variant string from a component's `params` map. Returns
 * undefined when no variant is set. Accepts the common keys
 * (`variant`, `package`, `wattage`) the components package uses for
 * sub-type discrimination.
 */
export function variantFromParams(params) {
    const candidates = ["variant", "package", "wattage"];
    for (const k of candidates) {
        const v = params[k];
        if (typeof v === "string" && v.length > 0)
            return v;
    }
    return undefined;
}
//# sourceMappingURL=metadata.js.map